/**
 * Maps the GetPaymentDetail `graphs[]` to the flow view model.
 *
 * Everything the view needs is derived from the payload: column placement from
 * each graph's edges, step order from each step's `rank`, inbound grouping from
 * each graph's `responseType`. States are passed through as reported and only
 * bucketed into a tone for colouring.
 */
import {
  ApiFlowGraph, ApiGraphNode, ApiGraphStep, ApiGraphEdge,
} from './paymentDetailApi';
import {
  Flow, FlowGraph, FlowNode, FlowStep, FlowEdge, FlowDroppedEvent, FlowTone,
} from './paymentDetailTypes';
import { bottleneckLabel, formatMs, timeOfDay, titleCase, toDirection } from './paymentDetailFormat';

/** The value as reported, or undefined when empty / the literal "null". */
const present = (value?: string | null): string | undefined =>
  value && value !== 'null' ? value : undefined;

/** Only SUCCESS and FAILED carry meaning here; anything else stays neutral. */
const toTone = (state?: string): FlowTone => {
  switch (present(state)?.toUpperCase()) {
    case 'SUCCESS': return 'success';
    case 'FAILED': return 'failed';
    default: return 'neutral';
  }
};

/** "×5" when several events collapsed into one step. */
const countLabel = (count?: number): string | undefined =>
  count != null && count > 1 ? `×${count}` : undefined;

/** Non-zero durations only — a 0ms step reads better with no duration at all. */
const duration = (ms?: number): string | undefined => (ms ? formatMs(ms) : undefined);

/** A step's time: a single stamp, or "first → last" when events collapsed. */
const stepTime = (step: ApiGraphStep): string | undefined => {
  const first = timeOfDay(step.eventTimestamp);
  if (first === '-') return undefined;
  const last = timeOfDay(step.lastEventTimestamp);
  return last === '-' || last === first ? first : `${first} → ${last}`;
};

/** `latencyMs` holds one entry per occurrence: show the value, or the spread. */
const edgeLatency = (latencies?: number[]): string | undefined => {
  const values = (latencies ?? []).filter(ms => ms != null && !Number.isNaN(ms));
  if (values.length === 0) return undefined;
  const [min, max] = [Math.min(...values), Math.max(...values)];
  return min === max ? formatMs(min) : `${formatMs(min)}–${formatMs(max)}`;
};

const toSteps = (node: ApiGraphNode, nodeId: string): FlowStep[] =>
  [...(node.steps ?? [])]
    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
    .map((step, index) => ({
      id: `${nodeId}-${step.rank ?? index}-${step.status}`,
      code: present(step.status) ?? '-',
      state: present(step.lastState),
      tone: toTone(step.lastState),
      countLabel: countLabel(step.count),
      gapFromPrevious: index === 0 ? undefined : formatMs(step.gapFromPreviousMs),
      at: stepTime(step),
    }));

const toNode = (graphId: string, node: ApiGraphNode, bottleneckSystem?: string): FlowNode => {
  const id = `${graphId}-${node.system}`;
  const steps = toSteps(node, id);
  // Steps are rank-ordered, so the last one is what the system ended on.
  const last = steps[steps.length - 1];
  return {
    id,
    system: node.system,
    state: last?.state,
    tone: last?.tone ?? 'neutral',
    lastStatus: last?.code,
    latency: duration(node.systemLatencyMs),
    passThrough: steps.length === 0,
    bottleneck: !!bottleneckSystem && node.system === bottleneckSystem,
    steps,
  };
};

/**
 * Groups node ids into columns so every edge points from one column to a later
 * one: roots start at column 0, every other node sits one past its deepest
 * parent (Kahn's algorithm, propagating depth). A chain becomes one node per
 * column and a fan-out becomes siblings. Self-loops, duplicate edges and edges
 * naming unknown nodes are ignored; nodes held up by a cycle are appended, so
 * nothing disappears from the view.
 */
const toLevels = (ids: string[], edges: FlowEdge[]): string[][] => {
  const known = new Set(ids);
  const children = new Map(ids.map(id => [id, new Set<string>()]));
  const inDegree = new Map(ids.map(id => [id, 0]));
  edges.forEach(({ fromId, toId }) => {
    if (fromId === toId || !known.has(fromId) || !known.has(toId)) return;
    const kids = children.get(fromId)!;
    if (kids.has(toId)) return; // a repeated handoff is still one dependency
    kids.add(toId);
    inDegree.set(toId, inDegree.get(toId)! + 1);
  });

  const depth = new Map(ids.map(id => [id, 0]));
  const settled = ids.filter(id => inDegree.get(id) === 0);
  for (let i = 0; i < settled.length; i += 1) {
    const id = settled[i];
    children.get(id)!.forEach(child => {
      depth.set(child, Math.max(depth.get(child)!, depth.get(id)! + 1));
      const remaining = inDegree.get(child)! - 1;
      inDegree.set(child, remaining);
      if (remaining === 0) settled.push(child);
    });
  }

  if (settled.length < ids.length) {
    const done = new Set(settled);
    const deepest = settled.reduce((max, id) => Math.max(max, depth.get(id)!), 0);
    const parked = settled.length === 0 ? deepest : deepest + 1;
    ids.forEach(id => { if (!done.has(id)) depth.set(id, parked); });
  }

  const levels: string[][] = [];
  ids.forEach(id => {
    const column = depth.get(id)!;
    while (levels.length <= column) levels.push([]);
    levels[column].push(id);
  });
  return levels.filter(level => level.length > 0);
};

const toGraph = (graph: ApiFlowGraph, index: number): FlowGraph => {
  const direction = toDirection(graph.direction);
  const responseType = present(graph.responseType);
  const id = `${direction}-${responseType ?? 'flow'}-${index}`.toLowerCase();

  const nodes = (graph.nodes ?? []).map(node => toNode(id, node, graph.bottleneck?.system));
  const idBySystem = new Map(nodes.map(node => [node.system, node.id]));
  const edges: FlowEdge[] = (graph.edges ?? [])
    .map((edge: ApiGraphEdge, edgeIndex) => ({
      id: `${id}-edge-${edgeIndex}`,
      fromId: idBySystem.get(edge.from) ?? edge.from,
      toId: idBySystem.get(edge.to) ?? edge.to,
      label: present(edge.status),
      latency: edgeLatency(edge.latencyMs),
      countLabel: countLabel(edge.count),
    }));

  const dropped: FlowDroppedEvent[] = (graph.dropped ?? []).map((event, eventIndex) => ({
    id: `${id}-dropped-${eventIndex}`,
    system: present(event.system) ?? '-',
    status: present(event.status) ?? '-',
    at: timeOfDay(event.eventTimestamp),
  }));

  return {
    id,
    title: responseType ?? titleCase(direction),
    direction,
    responseType,
    totalLatency: duration(graph.totalLatencyMs),
    bottleneckLabel: bottleneckLabel(graph.bottleneck),
    levels: toLevels(nodes.map(node => node.id), edges),
    nodes,
    edges,
    dropped,
  };
};

/* ------------------------------------------------------------------ */
/* Entry point                                                         */
/* ------------------------------------------------------------------ */

export const toFlow = (graphs: ApiFlowGraph[], totalLatencyMs?: number): Flow => {
  const mapped = (graphs ?? []).map(toGraph);
  const outbound = mapped.filter(graph => graph.direction === 'OUTBOUND');
  const inbound = mapped.filter(graph => graph.direction === 'INBOUND');

  // The wait at the bank is not reported: it is the leg latency the graphs
  // leave unaccounted for, so it only holds when both directions are present.
  let bankWait: string | undefined;
  if (outbound.length > 0 && inbound.length > 0 && totalLatencyMs != null) {
    const accounted = (graphs ?? []).reduce((sum, graph) => sum + (graph.totalLatencyMs ?? 0), 0);
    if (totalLatencyMs > accounted) bankWait = formatMs(totalLatencyMs - accounted);
  }

  return { outbound, inbound, bankWait };
};
